// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../AuthContext";
import { supabase } from "../supabase";

// ══════════════════════════════════════════════════════════════════
// CONSTANTS & DEFAULT DATA
// ══════════════════════════════════════════════════════════════════

const COLORS = {
  bg: "#0a0a0f", surface: "#111118", surfaceLight: "#1a1a24",
  border: "#2a2a3a", borderLight: "#3a3a4a",
  gold: "#d4a843", goldLight: "#e8c469", goldDark: "#b8922e",
  green: "#34d399", red: "#f87171", blue: "#60a5fa",
  text: "#f1f5f9", textMuted: "#94a3b8", textDim: "#64748b",
  accent: "#8b5cf6",
};

const fmt = (n) => {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtPct = (n) => `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;

// All 37 web apps with their industry, value per app, revenue multiplier
const DEFAULT_WEB_APPS = [
  { id: "budledger", name: "BudLedger", industry: "Cannabis", value: 95000 },
  { id: "closingdesk", name: "ClosingDesk", industry: "Real Estate", value: 95000 },
  { id: "croppatrol-broda", name: "CropPatrol (Broda WL)", industry: "Agriculture", value: 95000 },
  { id: "directional-ops", name: "Directional Ops", industry: "Oil & Gas", value: 95000 },
  { id: "frcc", name: "FRCC", industry: "Country Club", value: 95000 },
  { id: "florigen-nft", name: "FlorigenNFT", industry: "Blockchain/NFT", value: 95000 },
  { id: "forge-companion", name: "Forge Companion", industry: "AI Pet Care", value: 95000 },
  { id: "formgenius", name: "FormGenius", industry: "Forms/Docs", value: 95000 },
  { id: "glowdesk", name: "GlowDesk", industry: "Beauty", value: 95000 },
  { id: "haulsync", name: "Forge Fleet", industry: "Fleet Management", value: 95000 },
  { id: "herdledger", name: "HerdLedger", industry: "Cattle/Agriculture", value: 95000 },
  { id: "hoist-control", name: "LiftForge", industry: "Crane & Rigging", value: 95000 },
  { id: "innovation-hub", name: "The Forge Standard", industry: "IP Marketplace", value: 95000 },
  { id: "insurance-broker-iq", name: "PolicyForge", industry: "Insurance", value: 95000 },
  { id: "iod", name: "IOD", industry: "Overhead Doors", value: 95000 },
  { id: "ironledger", name: "IronLedger", industry: "Iron & Steel", value: 95000 },
  { id: "lotsync", name: "LotSync", industry: "Automotive", value: 95000 },
  { id: "mtiq", name: "MT Forge", industry: "Internal Platform", value: 95000 },
  { id: "nursevault", name: "NurseVault", industry: "Healthcare US", value: 95000 },
  { id: "nursevault-ca", name: "NurseVault CA", industry: "Healthcare CA", value: 95000 },
  { id: "oresync", name: "OreSync", industry: "Mining", value: 95000 },
  { id: "rigwarden", name: "RigForge", industry: "Rig Safety", value: 95000 },
  { id: "stagevault", name: "StageVault", industry: "Events", value: 95000 },
  { id: "towforge", name: "TowForge", industry: "Towing", value: 95000 },
  { id: "treadops", name: "TreadOps", industry: "Tire Shop", value: 95000 },
  { id: "vactraq", name: "VacTraq", industry: "Vac Hauling", value: 95000 },
  { id: "vantage-ops", name: "Vantage Ops", industry: "Service Trades", value: 95000 },
  { id: "wellnessiq", name: "VitaSync", industry: "Wellness", value: 95000 },
  { id: "wrenchsync", name: "WrenchSync", industry: "Mechanic", value: 95000 },
  { id: "funforge-arcade", name: "FunForge Arcade", industry: "Gaming", value: 95000 },
  { id: "game-factory", name: "GameFactory", industry: "Gaming", value: 95000 },
  { id: "game-of-fire", name: "Game of Fire", industry: "Education", value: 95000 },
  { id: "grid-hunter", name: "Grid Hunter", industry: "Gaming", value: 95000 },
  { id: "jingle-forge", name: "JingleForge", industry: "Music/Audio", value: 95000 },
  { id: "nexus-mayhem", name: "Nexus Mayhem", industry: "Gaming", value: 95000 },
  { id: "roblox-forge", name: "RobloxForge", industry: "Gaming", value: 95000 },
  { id: "forge-casino", name: "Forge Casino", industry: "Casino/Gambling", value: 95000 },
];

const DEFAULT_SPECIAL_ASSETS = [
  { id: "taktikalos", name: "TaktikalOS", desc: "37pp, 18 panels, Wellbore3D, WITS0, AutoDrive, rig gateway, audit chain", value: 5000000, postPatent: 25000000 },
  { id: "nexus-engine", name: "Nexus Engine", desc: "Rust ECS, wgpu/WebGPU/Vulkan, Rapier3D, WebTransport, GLTF", value: 3000000 },
  { id: "forge-companion-tech", name: "ForgeCompanionTech", desc: "20pp, VetChip biosensor, cortisol patent", value: 800000 },
  { id: "florigen-ip", name: "FlorigenNFT IP", desc: "13pp, 2D-to-3D NFT, provisional patent", value: 350000 },
  { id: "forge-casino-asset", name: "Forge Casino (Pre-License)", desc: "22pp, provably fair engine, compliance stack", value: 2000000, postLicense: 10000000 },
  { id: "forge-standard-platform", name: "The Forge Standard Platform", desc: "58pp, 3-sided marketplace, SEC EDGAR, 18-tool business suite", value: 1200000 },
  { id: "forge-token", name: "$FORGE Token Ecosystem", desc: "ForgeToken.sol, ForgeBurn.sol, IdeaVault.sol, PriceOracle.sol", value: 500000 },
  { id: "audit-chain-patent", name: "Audit Chain Patent (Pending)", desc: "SHA-256 hash chain, Ed25519 signing, external timestamps", value: 500000, postPatent: 5000000 },
];

const DEFAULT_INDUSTRIES = [
  "Agriculture", "Fleet Management", "Oil & Gas", "Automotive", "Healthcare US", "Healthcare CA",
  "Service Trades", "Cannabis", "Mining", "Vac Hauling", "Beauty", "Real Estate", "Mechanic",
  "Tire Shop", "Cattle/Agriculture", "Wellness", "Insurance", "Events", "Forms/Docs",
  "Crane & Rigging", "Rig Safety", "Towing", "Country Club", "AI Pet Care", "Blockchain/NFT",
  "Iron & Steel", "IP Marketplace", "Overhead Doors", "Gaming", "Education", "Casino/Gambling",
  "Music/Audio", "Internal Platform", "Pharmacy", "Senior Care",
];

const DEFAULT_CLIENTS = [
  { id: "broda", name: "BrodaFarms", app: "CropPatrol WL", status: "pilot", mrr: 0 },
  { id: "geerts", name: "GeertsFarms", app: "CropPatrol WL", status: "pilot", mrr: 0 },
  { id: "iod", name: "Island Overhead Doors", app: "IOD", status: "signed", mrr: 0 },
  { id: "canstorr", name: "Canstorr", app: "BudLedger", status: "signed", mrr: 0 },
  { id: "3way", name: "3 Way Industries", app: "Forge Fleet", status: "signed", mrr: 0 },
  { id: "rosebud", name: "Rosebud Cannabis Farms", app: "BudLedger", status: "signed", mrr: 0 },
  { id: "smf", name: "Steve Marshall Ford", app: "LotSync", status: "signed", mrr: 0 },
  { id: "mcintyre", name: "McIntyre Cranes", app: "LiftForge", status: "prospect", mrr: 0 },
  { id: "rant", name: "Rant Plumbing & Heating", app: "ClimateOps", status: "prospect", mrr: 0 },
  { id: "tactical", name: "Tactical Directional", app: "TaktikalOS WL", status: "pilot", mrr: 0 },
];

const DEFAULT_COUNTRIES = [
  { id: "ca", name: "Canada", status: "hq", multiplier: 1.0 },
  { id: "us", name: "United States", status: "active", multiplier: 1.0 },
];

const DEFAULT_REVENUE_STREAMS = [
  { id: "taktikalos-rigs", name: "TaktikalOS Per-Rig", unit: "rigs", count: 0, revenuePerUnit: 12500, desc: "$12.5K/rig/month (DD + consultant + geo + rig mgr)" },
  { id: "saas-subscriptions", name: "SaaS Subscriptions", unit: "clients", count: 0, revenuePerUnit: 500, desc: "Avg $500/mo/client across verticals" },
  { id: "forge-standard-biz", name: "Forge Standard Business Suite", unit: "businesses", count: 0, revenuePerUnit: 999, desc: "$999/mo Scale tier" },
  { id: "casino-revenue", name: "Forge Casino", unit: "monthly", count: 0, revenuePerUnit: 1, desc: "Post-license monthly take" },
  { id: "forge-credits", name: "$FORGE Credit Sales", unit: "monthly", count: 0, revenuePerUnit: 1, desc: "Platform credit purchases" },
  { id: "white-labels", name: "White-Label Fees", unit: "clients", count: 0, revenuePerUnit: 2500, desc: "$2.5K/mo per white-label" },
];

const DEFAULT_METRICS = {
  webApps: DEFAULT_WEB_APPS,
  mobileApps: 25,
  specialAssets: DEFAULT_SPECIAL_ASSETS,
  industries: DEFAULT_INDUSTRIES,
  clients: DEFAULT_CLIENTS,
  countries: DEFAULT_COUNTRIES,
  revenueStreams: DEFAULT_REVENUE_STREAMS,
  teamSize: 19,
  linesOfCode: 666146,
  totalFiles: 2933,
  totalPages: 1443,
  legalDocs: 109,
  pitchDecks: 34,
  patentClaims: 50,
  patentFilings: 3,
  costPerLOC: 28,
  costPerTeamMember: 175000,
  costPerLegalDoc: 3500,
  systemsCost: 250000,
  tamPerIndustry: 2500000000,
  tamPenetration: 0.0002,
  revenueMultiple: 8.5,
  forgeStandardLive: 1200000,
  forgeStandardBeta: 800000,
  forgeStandardConcept: 500000,
};

// ══════════════════════════════════════════════════════════════════
// VALUATION ENGINE
// ══════════════════════════════════════════════════════════════════

function calculateFullValuation(m) {
  // ── Method 1: Revenue Multiple (10% weight) ──
  const totalMRR = m.revenueStreams.reduce((sum, s) => {
    if (s.unit === "monthly") return sum + (s.count * s.revenuePerUnit);
    return sum + (s.count * s.revenuePerUnit);
  }, 0);
  const arr = totalMRR * 12;
  const revenueVal = arr * m.revenueMultiple;

  // ── Method 2: IP Portfolio Value (25% weight) ──
  const webAppVal = m.webApps.reduce((sum, a) => sum + a.value, 0);
  const mobileAppVal = m.mobileApps * 40000;
  const innovationModules = m.webApps.filter(a =>
    ["The Forge Standard", "MT Forge", "FunForge Arcade", "Forge Casino", "Game of Fire"].includes(a.name)
  ).length * 65000;
  const legalDocVal = m.legalDocs * 1500;
  const pitchDeckVal = m.pitchDecks * 2000;
  const specialAssetVal = m.specialAssets.reduce((sum, a) => sum + a.value, 0);
  const ipVal = webAppVal + mobileAppVal + innovationModules + legalDocVal + pitchDeckVal + specialAssetVal;

  // ── Method 3: Market Size TAM (20% weight) ──
  const marketVal = m.industries.length * m.tamPerIndustry * m.tamPenetration;

  // ── Method 4: Cost to Recreate (25% weight) ──
  const codeRecreateCost = m.linesOfCode * m.costPerLOC;
  const teamRecreateCost = m.teamSize * m.costPerTeamMember;
  const legalRecreateCost = m.legalDocs * m.costPerLegalDoc;
  const recreateVal = codeRecreateCost + teamRecreateCost + legalRecreateCost + m.systemsCost;

  // ── Method 5: The Forge Standard (20% weight) ──
  const liveApps = m.webApps.length;
  const forgeVal = liveApps * m.forgeStandardLive;

  // ── Weighted blend ──
  const blended =
    revenueVal * 0.10 +
    ipVal * 0.25 +
    marketVal * 0.20 +
    recreateVal * 0.25 +
    forgeVal * 0.20;

  // ── Premium multipliers ──
  let patentMult = 1.0;
  if (m.patentClaims >= 50) patentMult = 1.22;
  else if (m.patentClaims >= 15) patentMult = 1.18;
  else if (m.patentClaims >= 5) patentMult = 1.12;
  else if (m.patentClaims >= 1) patentMult = 1.08;

  let legalMult = 1.0;
  if (m.legalDocs >= 100) legalMult = 1.08;
  else if (m.legalDocs >= 50) legalMult = 1.05;

  let diversMult = 1.0;
  if (m.industries.length >= 20) diversMult = 1.06;
  else if (m.industries.length >= 10) diversMult = 1.03;

  const combinedPremium = patentMult * legalMult * diversMult;
  const total = Math.round(blended * combinedPremium);

  // ── Country multiplier ──
  const countryBoost = m.countries.reduce((sum, c) => sum + c.multiplier, 0) / Math.max(m.countries.length, 1);

  // ── Projections ──
  const clientCount = m.clients.length;
  const signedClients = m.clients.filter(c => c.status === "signed" || c.status === "active").length;

  return {
    total,
    totalMRR,
    arr,
    methods: [
      { label: "Revenue Multiple", value: Math.round(revenueVal), weight: 10, desc: `ARR ${fmt(arr)} x ${m.revenueMultiple}x`, color: "#60a5fa" },
      { label: "IP Portfolio", value: Math.round(ipVal), weight: 25, desc: `${m.webApps.length} web + ${m.mobileApps} mobile + ${m.specialAssets.length} special assets`, color: "#8b5cf6" },
      { label: "Market Size (TAM)", value: Math.round(marketVal), weight: 20, desc: `${m.industries.length} industries x $2.5B x 0.02%`, color: "#34d399" },
      { label: "Cost to Recreate", value: Math.round(recreateVal), weight: 25, desc: `${(m.linesOfCode/1000).toFixed(0)}K LOC + ${m.teamSize} team + ${m.legalDocs} docs`, color: "#f59e0b" },
      { label: "The Forge Standard", value: Math.round(forgeVal), weight: 20, desc: `${liveApps} live apps x $1.2M`, color: "#ec4899" },
    ],
    premiums: { patent: patentMult, legal: legalMult, diversification: diversMult, combined: combinedPremium },
    ipBreakdown: { webAppVal, mobileAppVal, innovationModules, legalDocVal, pitchDeckVal, specialAssetVal },
    recreateBreakdown: { codeRecreateCost, teamRecreateCost, legalRecreateCost, systemsCost: m.systemsCost },
    clientCount,
    signedClients,
    countryBoost,
  };
}

// ══════════════════════════════════════════════════════════════════
// REUSABLE COMPONENTS
// ══════════════════════════════════════════════════════════════════

function Card({ children, style, glow }) {
  return (
    <div style={{
      background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12,
      padding: "20px 24px", ...(glow ? { boxShadow: `0 0 30px ${COLORS.gold}20` } : {}), ...style,
    }}>
      {children}
    </div>
  );
}

function SectionHeader({ title, count, onAdd, addLabel }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, margin: 0 }}>{title}</h3>
        {count !== undefined && (
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.gold, background: `${COLORS.gold}20`, padding: "2px 8px", borderRadius: 6 }}>
            {count}
          </span>
        )}
      </div>
      {onAdd && (
        <button onClick={onAdd} style={{
          padding: "6px 14px", borderRadius: 8, border: `1px solid ${COLORS.gold}40`,
          background: `${COLORS.gold}10`, fontSize: 12, fontWeight: 600, cursor: "pointer",
          color: COLORS.gold, transition: "all 0.2s",
        }}>
          + {addLabel || "Add"}
        </button>
      )}
    </div>
  );
}

function MetricBadge({ label, value, color }) {
  return (
    <div style={{ background: COLORS.surfaceLight, borderRadius: 8, padding: "12px 16px", border: `1px solid ${COLORS.border}` }}>
      <div style={{ fontSize: 10, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || COLORS.text }}>{value}</div>
    </div>
  );
}

function MethodBar({ method }) {
  return (
    <div style={{ background: COLORS.surfaceLight, borderRadius: 8, padding: "14px 16px", borderLeft: `3px solid ${method.color}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{method.label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: method.color }}>{method.weight}%</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 4 }}>{fmt(method.value)}</div>
      <div style={{ fontSize: 11, color: COLORS.textDim }}>{method.desc}</div>
      <div style={{ height: 4, background: `${method.color}20`, borderRadius: 2, marginTop: 8, overflow: "hidden" }}>
        <div style={{ width: `${method.weight * 4}%`, height: "100%", background: method.color, borderRadius: 2 }} />
      </div>
    </div>
  );
}

function InlineTag({ children, color }) {
  return (
    <span style={{
      display: "inline-block", fontSize: 10, fontWeight: 700, color: color || COLORS.gold,
      background: `${color || COLORS.gold}15`, padding: "2px 8px", borderRadius: 4,
      textTransform: "uppercase", letterSpacing: 0.5,
    }}>
      {children}
    </span>
  );
}

function RemoveBtn({ onClick }) {
  return (
    <button onClick={onClick} style={{
      width: 22, height: 22, borderRadius: 6, border: `1px solid ${COLORS.red}30`,
      background: `${COLORS.red}10`, color: COLORS.red, fontSize: 12, cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1, flexShrink: 0,
    }}>
      x
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════
// MODAL
// ══════════════════════════════════════════════════════════════════

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center",
      backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16,
        padding: 32, maxWidth: 500, width: "90%", maxHeight: "80vh", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.text, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 20, cursor: "pointer" }}>x</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalInput({ label, value, onChange, type, placeholder }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>{label}</label>
      <input
        type={type || "text"} value={value} onChange={(e) => onChange(type === "number" ? (parseFloat(e.target.value) || 0) : e.target.value)}
        placeholder={placeholder || ""}
        style={{
          width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${COLORS.border}`,
          background: COLORS.surfaceLight, color: COLORS.text, fontSize: 14, boxSizing: "border-box",
          outline: "none",
        }}
      />
    </div>
  );
}

function ModalSelect({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>{label}</label>
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${COLORS.border}`,
          background: COLORS.surfaceLight, color: COLORS.text, fontSize: 14, boxSizing: "border-box",
          outline: "none",
        }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function ModalBtn({ onClick, children, primary }) {
  return (
    <button onClick={onClick} style={{
      padding: "10px 24px", borderRadius: 8, border: primary ? "none" : `1px solid ${COLORS.border}`,
      background: primary ? `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldDark})` : COLORS.surfaceLight,
      color: primary ? "#0a0a0f" : COLORS.text, fontSize: 14, fontWeight: 700, cursor: "pointer",
      transition: "all 0.2s",
    }}>
      {children}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════

export default function ValuationPage() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(DEFAULT_METRICS);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Modal states
  const [modal, setModal] = useState(null); // "country" | "client" | "app" | "industry" | "asset" | "revenue"
  const [modalData, setModalData] = useState({});
  const prevTotal = useRef(null);

  // ── Compute valuation ──
  const val = useMemo(() => calculateFullValuation(metrics), [metrics]);

  // Track value changes for animation
  useEffect(() => {
    prevTotal.current = val.total;
  }, [val.total]);

  // ── Load from Supabase ──
  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: latest } = await supabase
        .from("valuation_snapshots")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1);

      if (latest && latest.length > 0 && latest[0].metrics) {
        const saved = latest[0].metrics;
        setMetrics(prev => ({
          ...prev,
          ...saved,
          webApps: saved.webApps || prev.webApps,
          specialAssets: saved.specialAssets || prev.specialAssets,
          industries: saved.industries || prev.industries,
          clients: saved.clients || prev.clients,
          countries: saved.countries || prev.countries,
          revenueStreams: saved.revenueStreams || prev.revenueStreams,
        }));
      }

      const { data: hist } = await supabase
        .from("valuation_snapshots")
        .select("id, created_at, total_valuation, notes, change_reasons")
        .order("created_at", { ascending: false })
        .limit(90);

      if (hist) setHistory(hist);
    } catch (e) {
      console.error("Failed to load valuation data:", e);
    } finally {
      setLoading(false);
    }
  };

  // ── Save snapshot ──
  const saveSnapshot = async () => {
    setSaving(true);
    try {
      await supabase.from("valuation_snapshots").insert({
        total_valuation: val.total,
        metrics,
        notes: `Snapshot — ${new Date().toLocaleString()}`,
        change_percent: history.length > 0 && history[0].total_valuation > 0
          ? parseFloat(((val.total - history[0].total_valuation) / history[0].total_valuation * 100).toFixed(2))
          : 0,
        change_reasons: ["Manual snapshot"],
        recorded_by: user?.email,
        created_at: new Date().toISOString(),
      });
      await loadData();
    } catch (e) {
      console.error("Save failed:", e);
    } finally {
      setSaving(false);
    }
  };

  // ── Updater helpers ──
  const updateMetric = (key, value) => setMetrics(prev => ({ ...prev, [key]: value }));

  const addCountry = () => {
    const { name, status } = modalData;
    if (!name) return;
    updateMetric("countries", [...metrics.countries, {
      id: name.toLowerCase().replace(/\s/g, "-") + "-" + Date.now(),
      name, status: status || "target", multiplier: 1.0,
    }]);
    setModal(null);
    setModalData({});
  };

  const removeCountry = (id) => updateMetric("countries", metrics.countries.filter(c => c.id !== id));

  const addClient = () => {
    const { name, app, status } = modalData;
    if (!name) return;
    updateMetric("clients", [...metrics.clients, {
      id: name.toLowerCase().replace(/\s/g, "-") + "-" + Date.now(),
      name, app: app || "", status: status || "prospect", mrr: modalData.mrr || 0,
    }]);
    setModal(null);
    setModalData({});
  };

  const removeClient = (id) => updateMetric("clients", metrics.clients.filter(c => c.id !== id));

  const addApp = () => {
    const { name, industry } = modalData;
    if (!name) return;
    updateMetric("webApps", [...metrics.webApps, {
      id: name.toLowerCase().replace(/\s/g, "-") + "-" + Date.now(),
      name, industry: industry || "General", value: 95000,
    }]);
    setModal(null);
    setModalData({});
  };

  const removeApp = (id) => updateMetric("webApps", metrics.webApps.filter(a => a.id !== id));

  const addIndustry = () => {
    const { name } = modalData;
    if (!name || metrics.industries.includes(name)) return;
    updateMetric("industries", [...metrics.industries, name]);
    setModal(null);
    setModalData({});
  };

  const removeIndustry = (name) => updateMetric("industries", metrics.industries.filter(i => i !== name));

  const addSpecialAsset = () => {
    const { name, desc, value: assetVal } = modalData;
    if (!name) return;
    updateMetric("specialAssets", [...metrics.specialAssets, {
      id: name.toLowerCase().replace(/\s/g, "-") + "-" + Date.now(),
      name, desc: desc || "", value: assetVal || 0,
    }]);
    setModal(null);
    setModalData({});
  };

  const removeSpecialAsset = (id) => updateMetric("specialAssets", metrics.specialAssets.filter(a => a.id !== id));

  const addRevenueStream = () => {
    const { name, unit, revenuePerUnit, desc } = modalData;
    if (!name) return;
    updateMetric("revenueStreams", [...metrics.revenueStreams, {
      id: name.toLowerCase().replace(/\s/g, "-") + "-" + Date.now(),
      name, unit: unit || "monthly", count: 0, revenuePerUnit: revenuePerUnit || 0, desc: desc || "",
    }]);
    setModal(null);
    setModalData({});
  };

  const removeRevenueStream = (id) => updateMetric("revenueStreams", metrics.revenueStreams.filter(s => s.id !== id));

  const updateRevenueCount = (id, count) => {
    updateMetric("revenueStreams", metrics.revenueStreams.map(s =>
      s.id === id ? { ...s, count: parseFloat(count) || 0 } : s
    ));
  };

  const updateClientMRR = (id, mrr) => {
    updateMetric("clients", metrics.clients.map(c =>
      c.id === id ? { ...c, mrr: parseFloat(mrr) || 0 } : c
    ));
  };

  // ── Tab config ──
  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "apps", label: `Apps (${metrics.webApps.length})` },
    { id: "clients", label: `Clients (${metrics.clients.length})` },
    { id: "countries", label: `Countries (${metrics.countries.length})` },
    { id: "industries", label: `Industries (${metrics.industries.length})` },
    { id: "assets", label: `Assets (${metrics.specialAssets.length})` },
    { id: "revenue", label: "Revenue" },
    { id: "projections", label: "Projections" },
  ];

  // ── Styles ──
  const s = {
    page: { padding: "24px 32px", maxWidth: 1400, margin: "0 auto", background: COLORS.bg, minHeight: "100vh", color: COLORS.text },
    tabBar: { display: "flex", gap: 4, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 0, overflowX: "auto" },
    tab: (active) => ({
      padding: "10px 18px", fontSize: 13, fontWeight: active ? 700 : 500, cursor: "pointer",
      color: active ? COLORS.gold : COLORS.textDim, background: "none", border: "none",
      borderBottom: active ? `2px solid ${COLORS.gold}` : "2px solid transparent",
      transition: "all 0.2s", whiteSpace: "nowrap",
    }),
    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
    grid3: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 },
    grid4: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 },
    listRow: (i) => ({
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "10px 14px", background: i % 2 === 0 ? COLORS.surfaceLight : "transparent",
      borderRadius: 6, gap: 12,
    }),
  };

  if (loading) {
    return (
      <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 12, color: COLORS.gold }}>&#9881;</div>
          <p style={{ color: COLORS.textDim }}>Loading valuation engine...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* ══ HEADER ═══════════════════════════════════════════════ */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.text, margin: 0 }}>Valuation Calculator</h1>
          <p style={{ fontSize: 13, color: COLORS.textDim, margin: "4px 0 0" }}>
            Miskulin Technologies — Real-Time Asset Valuation Engine
          </p>
        </div>
        <button onClick={saveSnapshot} disabled={saving} style={{
          padding: "10px 20px", borderRadius: 8, border: "none",
          background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldDark})`,
          fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#0a0a0f",
          opacity: saving ? 0.6 : 1,
        }}>
          {saving ? "Saving..." : "Save Snapshot"}
        </button>
      </div>

      {/* ══ HEADLINE VALUATION ═══════════════════════════════════ */}
      <Card glow style={{
        marginBottom: 24,
        background: `linear-gradient(135deg, ${COLORS.bg} 0%, #141420 50%, ${COLORS.bg} 100%)`,
        border: `1px solid ${COLORS.gold}30`, padding: "32px 40px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginBottom: 6 }}>
              Estimated Company Valuation
            </div>
            <div style={{ fontSize: 52, fontWeight: 900, color: COLORS.gold, lineHeight: 1.1, fontFamily: "Georgia, serif" }}>
              {fmt(val.total)}
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <InlineTag color={COLORS.green}>MRR: {fmt(val.totalMRR)}</InlineTag>
              <InlineTag color={COLORS.blue}>ARR: {fmt(val.arr)}</InlineTag>
              <InlineTag color={COLORS.accent}>Premium: {((val.premiums.combined - 1) * 100).toFixed(1)}%</InlineTag>
              <InlineTag>{metrics.webApps.length} Apps</InlineTag>
              <InlineTag>{metrics.industries.length} Industries</InlineTag>
              <InlineTag>{metrics.clients.length} Clients</InlineTag>
              <InlineTag>{metrics.countries.length} Countries</InlineTag>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 4 }}>Premium Multipliers</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
              <span style={{ fontSize: 12, color: COLORS.textMuted }}>Patent: <strong style={{ color: COLORS.gold }}>{val.premiums.patent.toFixed(2)}x</strong></span>
              <span style={{ fontSize: 12, color: COLORS.textMuted }}>Legal: <strong style={{ color: COLORS.gold }}>{val.premiums.legal.toFixed(2)}x</strong></span>
              <span style={{ fontSize: 12, color: COLORS.textMuted }}>Diversification: <strong style={{ color: COLORS.gold }}>{val.premiums.diversification.toFixed(2)}x</strong></span>
              <span style={{ fontSize: 14, fontWeight: 800, color: COLORS.gold, marginTop: 4, borderTop: `1px solid ${COLORS.border}`, paddingTop: 4 }}>
                Combined: {val.premiums.combined.toFixed(3)}x
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* ══ TAB BAR ═══════════════════════════════════════════════ */}
      <div style={s.tabBar}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={s.tab(activeTab === t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* OVERVIEW TAB                                              */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === "overview" && (
        <>
          {/* Key Metrics */}
          <div style={{ ...s.grid4, marginBottom: 24 }}>
            <MetricBadge label="Web Apps" value={metrics.webApps.length} color={COLORS.gold} />
            <MetricBadge label="Mobile Apps" value={metrics.mobileApps} color={COLORS.blue} />
            <MetricBadge label="Industries" value={metrics.industries.length} color={COLORS.green} />
            <MetricBadge label="Team Size" value={metrics.teamSize} />
            <MetricBadge label="Lines of Code" value={`${(metrics.linesOfCode / 1000).toFixed(0)}K`} />
            <MetricBadge label="Legal Docs" value={metrics.legalDocs} />
            <MetricBadge label="Patent Claims" value={metrics.patentClaims} color={COLORS.accent} />
            <MetricBadge label="Clients" value={metrics.clients.length} color={COLORS.gold} />
          </div>

          {/* 5-Method Breakdown */}
          <Card style={{ marginBottom: 24 }}>
            <SectionHeader title="5-Method Blended Valuation" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
              {val.methods.map((m, i) => <MethodBar key={i} method={m} />)}
            </div>
          </Card>

          {/* Quick Edit Metrics */}
          <Card style={{ marginBottom: 24 }}>
            <SectionHeader title="Quick Edit Metrics" />
            <div style={{ ...s.grid3 }}>
              {[
                { key: "mobileApps", label: "Mobile Apps", val: metrics.mobileApps },
                { key: "teamSize", label: "Team Size", val: metrics.teamSize },
                { key: "linesOfCode", label: "Lines of Code", val: metrics.linesOfCode },
                { key: "legalDocs", label: "Legal Docs", val: metrics.legalDocs },
                { key: "pitchDecks", label: "Pitch Decks", val: metrics.pitchDecks },
                { key: "patentClaims", label: "Patent Claims", val: metrics.patentClaims },
                { key: "patentFilings", label: "Patent Filings", val: metrics.patentFilings },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: COLORS.textDim, marginBottom: 4 }}>{f.label}</label>
                  <input
                    type="number" value={f.val}
                    onChange={(e) => updateMetric(f.key, parseFloat(e.target.value) || 0)}
                    style={{
                      width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`,
                      background: COLORS.surfaceLight, color: COLORS.text, fontSize: 14, boxSizing: "border-box",
                    }}
                  />
                </div>
              ))}
            </div>
          </Card>

          {/* IP Breakdown */}
          <Card style={{ marginBottom: 24 }}>
            <SectionHeader title="IP Portfolio Breakdown" />
            <div style={{ ...s.grid3 }}>
              <MetricBadge label="Web App IP" value={fmt(val.ipBreakdown.webAppVal)} color={COLORS.blue} />
              <MetricBadge label="Mobile App IP" value={fmt(val.ipBreakdown.mobileAppVal)} color={COLORS.green} />
              <MetricBadge label="Innovation Modules" value={fmt(val.ipBreakdown.innovationModules)} color={COLORS.accent} />
              <MetricBadge label="Legal Doc IP" value={fmt(val.ipBreakdown.legalDocVal)} />
              <MetricBadge label="Pitch Deck IP" value={fmt(val.ipBreakdown.pitchDeckVal)} />
              <MetricBadge label="Special Assets" value={fmt(val.ipBreakdown.specialAssetVal)} color={COLORS.gold} />
            </div>
          </Card>

          {/* Cost to Recreate Breakdown */}
          <Card>
            <SectionHeader title="Cost to Recreate Breakdown" />
            <div style={{ ...s.grid4 }}>
              <MetricBadge label={`Code (${(metrics.linesOfCode/1000).toFixed(0)}K x $${metrics.costPerLOC})`} value={fmt(val.recreateBreakdown.codeRecreateCost)} />
              <MetricBadge label={`Team (${metrics.teamSize} x $${(metrics.costPerTeamMember/1000).toFixed(0)}K)`} value={fmt(val.recreateBreakdown.teamRecreateCost)} />
              <MetricBadge label={`Legal (${metrics.legalDocs} x $${metrics.costPerLegalDoc})`} value={fmt(val.recreateBreakdown.legalRecreateCost)} />
              <MetricBadge label="Systems Cost" value={fmt(val.recreateBreakdown.systemsCost)} />
            </div>
          </Card>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* APPS TAB                                                  */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === "apps" && (
        <Card>
          <SectionHeader title="Web Applications" count={metrics.webApps.length} onAdd={() => { setModalData({}); setModal("app"); }} addLabel="Add App" />
          <div style={{ maxHeight: 600, overflowY: "auto" }}>
            {metrics.webApps.map((app, i) => (
              <div key={app.id} style={s.listRow(i)}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{app.name}</span>
                  <span style={{ fontSize: 11, color: COLORS.textDim, marginLeft: 8 }}>{app.industry}</span>
                </div>
                <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.gold }}>{fmt(app.value)}</span>
                <RemoveBtn onClick={() => removeApp(app.id)} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, padding: 12, background: `${COLORS.gold}10`, borderRadius: 8, borderLeft: `3px solid ${COLORS.gold}` }}>
            <span style={{ fontSize: 13, color: COLORS.textMuted }}>Total Web App IP: </span>
            <span style={{ fontSize: 16, fontWeight: 800, color: COLORS.gold }}>{fmt(val.ipBreakdown.webAppVal)}</span>
            <span style={{ fontSize: 12, color: COLORS.textDim, marginLeft: 12 }}>+ {metrics.mobileApps} mobile apps ({fmt(val.ipBreakdown.mobileAppVal)})</span>
          </div>
        </Card>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* CLIENTS TAB                                               */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === "clients" && (
        <Card>
          <SectionHeader title="Clients & Pilots" count={metrics.clients.length} onAdd={() => { setModalData({}); setModal("client"); }} addLabel="Add Client" />
          <div style={{ ...s.grid4, marginBottom: 16 }}>
            <MetricBadge label="Total Clients" value={metrics.clients.length} color={COLORS.gold} />
            <MetricBadge label="Signed" value={metrics.clients.filter(c => c.status === "signed").length} color={COLORS.green} />
            <MetricBadge label="Pilots" value={metrics.clients.filter(c => c.status === "pilot").length} color={COLORS.blue} />
            <MetricBadge label="Prospects" value={metrics.clients.filter(c => c.status === "prospect").length} color={COLORS.textDim} />
          </div>
          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            {metrics.clients.map((client, i) => (
              <div key={client.id} style={s.listRow(i)}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{client.name}</span>
                  <span style={{ fontSize: 11, color: COLORS.textDim, marginLeft: 8 }}>{client.app}</span>
                </div>
                <InlineTag color={client.status === "signed" ? COLORS.green : client.status === "pilot" ? COLORS.blue : COLORS.textDim}>
                  {client.status}
                </InlineTag>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 11, color: COLORS.textDim }}>MRR $</span>
                  <input
                    type="number" value={client.mrr} onChange={(e) => updateClientMRR(client.id, e.target.value)}
                    style={{
                      width: 80, padding: "4px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`,
                      background: COLORS.surfaceLight, color: COLORS.gold, fontSize: 13, textAlign: "right",
                    }}
                  />
                </div>
                <RemoveBtn onClick={() => removeClient(client.id)} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* COUNTRIES TAB                                             */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === "countries" && (
        <Card>
          <SectionHeader title="Geographic Expansion" count={metrics.countries.length} onAdd={() => { setModalData({}); setModal("country"); }} addLabel="Add Country" />
          <p style={{ fontSize: 12, color: COLORS.textDim, marginBottom: 16 }}>
            Each country adds market multiplier. New markets increase TAM penetration potential and diversification premium.
          </p>
          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            {metrics.countries.map((country, i) => (
              <div key={country.id} style={s.listRow(i)}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{country.name}</span>
                </div>
                <InlineTag color={country.status === "hq" ? COLORS.gold : country.status === "active" ? COLORS.green : COLORS.blue}>
                  {country.status}
                </InlineTag>
                <span style={{ fontSize: 13, color: COLORS.textMuted }}>{country.multiplier}x</span>
                <RemoveBtn onClick={() => removeCountry(country.id)} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* INDUSTRIES TAB                                            */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === "industries" && (
        <Card>
          <SectionHeader title="Industry Verticals" count={metrics.industries.length} onAdd={() => { setModalData({}); setModal("industry"); }} addLabel="Add Industry" />
          <div style={{ marginBottom: 16, padding: 12, background: `${COLORS.gold}10`, borderRadius: 8, borderLeft: `3px solid ${COLORS.gold}` }}>
            <span style={{ fontSize: 12, color: COLORS.textMuted }}>TAM Contribution: </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.gold }}>
              {metrics.industries.length} x $2.5B x 0.02% = {fmt(metrics.industries.length * 2500000000 * 0.0002)}
            </span>
            <span style={{ fontSize: 12, color: COLORS.textDim, marginLeft: 12 }}>
              Diversification premium: {val.premiums.diversification}x
            </span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 500, overflowY: "auto" }}>
            {metrics.industries.map((ind, i) => (
              <div key={ind} style={{
                display: "flex", alignItems: "center", gap: 6, padding: "8px 12px",
                background: COLORS.surfaceLight, borderRadius: 8, border: `1px solid ${COLORS.border}`,
              }}>
                <span style={{ fontSize: 13, color: COLORS.text }}>{ind}</span>
                <RemoveBtn onClick={() => removeIndustry(ind)} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* ASSETS TAB                                                */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === "assets" && (
        <Card>
          <SectionHeader title="Special Assets" count={metrics.specialAssets.length} onAdd={() => { setModalData({}); setModal("asset"); }} addLabel="Add Asset" />
          <div style={{ maxHeight: 600, overflowY: "auto" }}>
            {metrics.specialAssets.map((asset, i) => (
              <div key={asset.id} style={{ ...s.listRow(i), flexDirection: "column", alignItems: "stretch" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{asset.name}</span>
                    {asset.postPatent && (
                      <span style={{ fontSize: 11, color: COLORS.accent, marginLeft: 8 }}>Post-Patent: {fmt(asset.postPatent)}</span>
                    )}
                    {asset.postLicense && (
                      <span style={{ fontSize: 11, color: COLORS.green, marginLeft: 8 }}>Post-License: {fmt(asset.postLicense)}</span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: COLORS.gold }}>{fmt(asset.value)}</span>
                    <RemoveBtn onClick={() => removeSpecialAsset(asset.id)} />
                  </div>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>{asset.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, padding: 12, background: `${COLORS.gold}10`, borderRadius: 8, borderLeft: `3px solid ${COLORS.gold}` }}>
            <span style={{ fontSize: 13, color: COLORS.textMuted }}>Total Special Asset Value: </span>
            <span style={{ fontSize: 18, fontWeight: 800, color: COLORS.gold }}>{fmt(val.ipBreakdown.specialAssetVal)}</span>
          </div>
        </Card>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* REVENUE TAB                                               */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === "revenue" && (
        <Card>
          <SectionHeader title="Revenue Streams" count={metrics.revenueStreams.length} onAdd={() => { setModalData({}); setModal("revenue"); }} addLabel="Add Stream" />
          <div style={{ ...s.grid3, marginBottom: 16 }}>
            <MetricBadge label="Total MRR" value={fmt(val.totalMRR)} color={COLORS.green} />
            <MetricBadge label="Total ARR" value={fmt(val.arr)} color={COLORS.gold} />
            <MetricBadge label="Revenue Method Value" value={fmt(val.methods[0].value)} color={COLORS.blue} />
          </div>
          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            {metrics.revenueStreams.map((stream, i) => {
              const streamMRR = stream.count * stream.revenuePerUnit;
              return (
                <div key={stream.id} style={{ ...s.listRow(i), flexDirection: "column", alignItems: "stretch" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{stream.name}</span>
                      <span style={{ fontSize: 11, color: COLORS.textDim, marginLeft: 8 }}>{fmt(stream.revenuePerUnit)}/{stream.unit}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, color: COLORS.textDim }}>{stream.unit}:</span>
                      <input
                        type="number" value={stream.count}
                        onChange={(e) => updateRevenueCount(stream.id, e.target.value)}
                        style={{
                          width: 80, padding: "6px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`,
                          background: COLORS.surfaceLight, color: COLORS.gold, fontSize: 14, fontWeight: 700, textAlign: "right",
                        }}
                      />
                      <span style={{ fontSize: 14, fontWeight: 700, color: streamMRR > 0 ? COLORS.green : COLORS.textDim, minWidth: 80, textAlign: "right" }}>
                        {fmt(streamMRR)}/mo
                      </span>
                      <RemoveBtn onClick={() => removeRevenueStream(stream.id)} />
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>{stream.desc}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* PROJECTIONS TAB                                           */}
      {/* ══════════════════════════════════════════════════════════ */}
      {activeTab === "projections" && (
        <>
          <Card style={{ marginBottom: 24 }}>
            <SectionHeader title="Valuation Projections" />
            <p style={{ fontSize: 12, color: COLORS.textDim, marginBottom: 16 }}>
              Based on current metrics and growth trajectory. Value only goes UP.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
              {[
                { label: "Today", value: val.total, color: COLORS.gold, desc: "Current blended valuation" },
                { label: "Q2 2026", value: Math.max(val.total * 1.5, 120000000), color: COLORS.green, desc: "IOD live, TaktikalOS on rigs, first revenue" },
                { label: "End of 2026", value: Math.max(val.total * 2.5, 200000000), color: COLORS.blue, desc: "Patents filed, 30+ clients, casino licensed" },
                { label: "End of 2027", value: Math.max(val.total * 6, 500000000), color: COLORS.accent, desc: "Licensing deals, marketplace live, $FORGE token" },
                { label: "At Scale", value: Math.max(val.total * 12, 1000000000), color: "#ec4899", desc: "1K wells + casino + insurance cascade + all verticals" },
              ].map((p, i) => (
                <div key={i} style={{
                  background: COLORS.surfaceLight, borderRadius: 10, padding: 20,
                  borderTop: `3px solid ${p.color}`, textAlign: "center",
                }}>
                  <div style={{ fontSize: 11, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 8 }}>{p.label}</div>
                  <div style={{ fontSize: 28, fontWeight: 900, color: p.color, marginBottom: 6 }}>{fmt(p.value)}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>{p.desc}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* TaktikalOS Revenue Model */}
          <Card style={{ marginBottom: 24 }}>
            <SectionHeader title="TaktikalOS Revenue Model" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
              {[
                { wells: 1, label: "1 Rig" },
                { wells: 10, label: "10 Rigs" },
                { wells: 100, label: "100 Wells" },
                { wells: 500, label: "500 Wells" },
                { wells: 1000, label: "1,000 Wells" },
              ].map((tier, i) => (
                <div key={i} style={{ background: COLORS.surfaceLight, borderRadius: 8, padding: 16, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: COLORS.textDim, fontWeight: 600 }}>{tier.label}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>MRR: {fmt(tier.wells * 12500)}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.gold, marginTop: 4 }}>ARR: {fmt(tier.wells * 12500 * 12)}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Insurance Cascade Model */}
          <Card style={{ marginBottom: 24 }}>
            <SectionHeader title="Insurance Cascade Distribution Model" />
            <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 24 }}>&#8594;</span>
                <span>Insurance agents adopt PolicyForge</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, paddingLeft: 20 }}>
                <span style={{ fontSize: 24 }}>&#8594;</span>
                <span>See TaktikalOS safety/compliance data from oil companies</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, paddingLeft: 40 }}>
                <span style={{ fontSize: 24 }}>&#8594;</span>
                <span>Realize: reduces liability for ALL their clients</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, paddingLeft: 60 }}>
                <span style={{ fontSize: 24 }}>&#8594;</span>
                <span>Recommend to ALL clients (crane, tow, fleet, field service, etc.)</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 80 }}>
                <span style={{ fontSize: 24 }}>&#8594;</span>
                <span style={{ color: COLORS.gold, fontWeight: 700 }}>CAC = near zero. Insurance companies become unpaid sales force.</span>
              </div>
            </div>
          </Card>

          {/* Valuation History */}
          <Card>
            <SectionHeader title="Valuation History" count={history.length} />
            {history.length === 0 ? (
              <div style={{ textAlign: "center", padding: 32, color: COLORS.textDim }}>
                <p>No snapshots yet. Click "Save Snapshot" to start tracking.</p>
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: "auto" }}>
                {history.map((entry, i) => {
                  const prevVal = history[i + 1]?.total_valuation || entry.total_valuation;
                  const change = entry.total_valuation - prevVal;
                  const isUp = change >= 0;
                  return (
                    <div key={entry.id} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: i < history.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                      <div style={{ minWidth: 80, textAlign: "right" }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted }}>
                          {new Date(entry.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </div>
                      </div>
                      <div style={{ width: 3, background: isUp ? COLORS.green : change === 0 ? COLORS.border : COLORS.red, borderRadius: 2, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                          <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{fmt(entry.total_valuation)}</span>
                          {change !== 0 && (
                            <span style={{ fontSize: 12, fontWeight: 600, color: isUp ? COLORS.green : COLORS.red }}>
                              {isUp ? "+" : ""}{fmt(change)}
                            </span>
                          )}
                        </div>
                        {entry.notes && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>{entry.notes}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* MODALS                                                    */}
      {/* ══════════════════════════════════════════════════════════ */}

      {/* Add Country Modal */}
      <Modal open={modal === "country"} onClose={() => setModal(null)} title="Add Country">
        <ModalInput label="Country Name" value={modalData.name || ""} onChange={(v) => setModalData(p => ({ ...p, name: v }))} placeholder="e.g. Australia" />
        <ModalSelect label="Status" value={modalData.status || "target"} onChange={(v) => setModalData(p => ({ ...p, status: v }))} options={[
          { value: "target", label: "Target Market" },
          { value: "active", label: "Active Operations" },
          { value: "hq", label: "Headquarters" },
        ]} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <ModalBtn onClick={() => setModal(null)}>Cancel</ModalBtn>
          <ModalBtn primary onClick={addCountry}>Add Country</ModalBtn>
        </div>
      </Modal>

      {/* Add Client Modal */}
      <Modal open={modal === "client"} onClose={() => setModal(null)} title="Add Client / Business">
        <ModalInput label="Company Name" value={modalData.name || ""} onChange={(v) => setModalData(p => ({ ...p, name: v }))} placeholder="e.g. Smith Industries" />
        <ModalInput label="App / Product" value={modalData.app || ""} onChange={(v) => setModalData(p => ({ ...p, app: v }))} placeholder="e.g. Forge Fleet" />
        <ModalSelect label="Status" value={modalData.status || "prospect"} onChange={(v) => setModalData(p => ({ ...p, status: v }))} options={[
          { value: "prospect", label: "Prospect" },
          { value: "pilot", label: "Pilot" },
          { value: "signed", label: "Signed" },
          { value: "active", label: "Active (Paying)" },
        ]} />
        <ModalInput label="Monthly Revenue ($)" value={modalData.mrr || ""} onChange={(v) => setModalData(p => ({ ...p, mrr: v }))} type="number" placeholder="0" />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <ModalBtn onClick={() => setModal(null)}>Cancel</ModalBtn>
          <ModalBtn primary onClick={addClient}>Add Client</ModalBtn>
        </div>
      </Modal>

      {/* Add App Modal */}
      <Modal open={modal === "app"} onClose={() => setModal(null)} title="Add Web Application">
        <ModalInput label="App Name" value={modalData.name || ""} onChange={(v) => setModalData(p => ({ ...p, name: v }))} placeholder="e.g. PharmForge" />
        <ModalInput label="Industry" value={modalData.industry || ""} onChange={(v) => setModalData(p => ({ ...p, industry: v }))} placeholder="e.g. Pharmacy" />
        <div style={{ marginTop: 8, padding: 12, background: `${COLORS.gold}10`, borderRadius: 8 }}>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>Each new web app adds: </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.gold }}>+$95K IP value + $1.2M Forge Standard value</span>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <ModalBtn onClick={() => setModal(null)}>Cancel</ModalBtn>
          <ModalBtn primary onClick={addApp}>Add App</ModalBtn>
        </div>
      </Modal>

      {/* Add Industry Modal */}
      <Modal open={modal === "industry"} onClose={() => setModal(null)} title="Add Industry Vertical">
        <ModalInput label="Industry Name" value={modalData.name || ""} onChange={(v) => setModalData(p => ({ ...p, name: v }))} placeholder="e.g. Construction" />
        <div style={{ marginTop: 8, padding: 12, background: `${COLORS.gold}10`, borderRadius: 8 }}>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>Each new industry adds: </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.gold }}>+$500K to TAM method ($2.5B x 0.02%)</span>
          {metrics.industries.length >= 19 && (
            <div style={{ fontSize: 11, color: COLORS.green, marginTop: 4 }}>
              At 20+ industries: diversification premium jumps to 1.06x
            </div>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <ModalBtn onClick={() => setModal(null)}>Cancel</ModalBtn>
          <ModalBtn primary onClick={addIndustry}>Add Industry</ModalBtn>
        </div>
      </Modal>

      {/* Add Special Asset Modal */}
      <Modal open={modal === "asset"} onClose={() => setModal(null)} title="Add Special Asset">
        <ModalInput label="Asset Name" value={modalData.name || ""} onChange={(v) => setModalData(p => ({ ...p, name: v }))} placeholder="e.g. PharmForge Patent" />
        <ModalInput label="Description" value={modalData.desc || ""} onChange={(v) => setModalData(p => ({ ...p, desc: v }))} placeholder="What is this asset?" />
        <ModalInput label="Current Value ($)" value={modalData.value || ""} onChange={(v) => setModalData(p => ({ ...p, value: v }))} type="number" placeholder="0" />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <ModalBtn onClick={() => setModal(null)}>Cancel</ModalBtn>
          <ModalBtn primary onClick={addSpecialAsset}>Add Asset</ModalBtn>
        </div>
      </Modal>

      {/* Add Revenue Stream Modal */}
      <Modal open={modal === "revenue"} onClose={() => setModal(null)} title="Add Revenue Stream">
        <ModalInput label="Stream Name" value={modalData.name || ""} onChange={(v) => setModalData(p => ({ ...p, name: v }))} placeholder="e.g. Enterprise Licensing" />
        <ModalInput label="Revenue Per Unit ($)" value={modalData.revenuePerUnit || ""} onChange={(v) => setModalData(p => ({ ...p, revenuePerUnit: v }))} type="number" placeholder="0" />
        <ModalSelect label="Unit Type" value={modalData.unit || "monthly"} onChange={(v) => setModalData(p => ({ ...p, unit: v }))} options={[
          { value: "monthly", label: "Monthly" },
          { value: "clients", label: "Per Client" },
          { value: "rigs", label: "Per Rig" },
          { value: "users", label: "Per User" },
        ]} />
        <ModalInput label="Description" value={modalData.desc || ""} onChange={(v) => setModalData(p => ({ ...p, desc: v }))} placeholder="Optional description" />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <ModalBtn onClick={() => setModal(null)}>Cancel</ModalBtn>
          <ModalBtn primary onClick={addRevenueStream}>Add Stream</ModalBtn>
        </div>
      </Modal>
    </div>
  );
}
